(function () {
  const SELECTORS = {
    root: '[data-mpr-root]',
    viewport: '[data-mpr-viewport]',
    track: '[data-mpr-track]',
    group: '[data-mpr-group]',
    prev: '[data-mpr-prev]',
    next: '[data-mpr-next]'
  };

  class MainPillarRibbon {
    constructor(root) {
      this.root = root;
      this.viewport = root.querySelector(SELECTORS.viewport);
      this.track = root.querySelector(SELECTORS.track);
      this.prevButton = root.querySelector(SELECTORS.prev);
      this.nextButton = root.querySelector(SELECTORS.next);
      this.loop = root.dataset.loop === 'true';
      this.activeIndex = 0;
      this.isProgrammatic = false;

      if (!this.viewport || !this.track) return;

      this.bindEvents();
      this.refresh();
      this.update();
    }

    get groups() {
      return Array.from(this.track.querySelectorAll(SELECTORS.group)).filter((group) => {
        return group.offsetParent !== null;
      });
    }

    bindEvents() {
      this.prevButton && this.prevButton.addEventListener('click', () => this.goTo(this.activeIndex - 1));
      this.nextButton && this.nextButton.addEventListener('click', () => this.goTo(this.activeIndex + 1));

      this.viewport.addEventListener('scroll', () => {
        if (this.scrollRaf) cancelAnimationFrame(this.scrollRaf);
        this.scrollRaf = requestAnimationFrame(() => this.updateFromScroll());
      }, { passive: true });

      this.viewport.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.goTo(this.activeIndex + 1);
        }
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.goTo(this.activeIndex - 1);
        }
      });

      window.addEventListener('resize', () => {
        if (this.resizeRaf) cancelAnimationFrame(this.resizeRaf);
        this.resizeRaf = requestAnimationFrame(() => {
          this.refresh();
          this.goTo(this.activeIndex, false);
        });
      });

      document.addEventListener('shopify:section:load', (event) => {
        if (event.target && event.target.contains(this.root)) {
          this.refresh();
          this.update();
        }
      });
    }

    refresh() {
      const groups = this.groups;
      if (!groups.length) return;

      this.activeIndex = Math.min(this.activeIndex, groups.length - 1);

      groups.forEach((group, index) => {
        group.dataset.index = String(index);
        group.setAttribute('aria-roledescription', 'slide');
        group.classList.toggle('is-last-visible', index === groups.length - 1);
      });
    }

    isMobile() {
      return window.matchMedia('(max-width: 749px)').matches;
    }

    previewWidth() {
      const styles = getComputedStyle(this.root);
      const variable = this.isMobile() ? '--mpr-mobile-preview-width' : '--mpr-desktop-preview-width';
      return parseFloat(styles.getPropertyValue(variable)) || 0;
    }

    groupGap() {
      const styles = getComputedStyle(this.root);
      const variable = this.isMobile() ? '--mpr-mobile-group-gap' : '--mpr-desktop-group-gap';
      return parseFloat(styles.getPropertyValue(variable)) || 0;
    }

    mainPaddingLeft() {
      const styles = getComputedStyle(this.root);
      const variable = this.isMobile() ? '--mpr-mobile-main-padding-left' : '--mpr-desktop-main-padding-left';
      return parseFloat(styles.getPropertyValue(variable)) || 0;
    }

    lastPaddingRight() {
      const styles = getComputedStyle(this.root);
      const variable = this.isMobile() ? '--mpr-mobile-last-padding-right' : '--mpr-desktop-last-padding-right';
      return parseFloat(styles.getPropertyValue(variable)) || 0;
    }

    desiredScrollLeft(index) {
      const groups = this.groups;
      const group = groups[index];
      if (!group) return 0;

      const isLast = index === groups.length - 1;
      const isFirst = index === 0;
      const preview = this.previewWidth();
      const gap = this.groupGap();
      const leftPadding = this.mainPaddingLeft ? this.mainPaddingLeft() : 0;
      const lastRightPadding = this.lastPaddingRight ? this.lastPaddingRight() : 0;

      let left = 0;

      if (isFirst) {
        // Group 1 stays at true start so the left track padding remains visible.
        left = 0;
      } else if (isLast) {
        // Final group aligns by its right edge.
        // The margin-right on .is-last-visible creates enough scrollable room for this to work.
        left = group.offsetLeft + group.offsetWidth - this.viewport.clientWidth + lastRightPadding;
      } else if (this.isMobile()) {
        // Middle mobile groups show next preview only.
        left = group.offsetLeft - leftPadding;
      } else {
        // Desktop middle groups show previous preview.
        left = group.offsetLeft - preview - gap - leftPadding;
      }

      return Math.max(0, Math.min(left, this.viewport.scrollWidth - this.viewport.clientWidth));
    }

    goTo(index, smooth = true) {
      const groups = this.groups;
      if (!groups.length) return;

      if (this.loop) {
        if (index < 0) index = groups.length - 1;
        if (index >= groups.length) index = 0;
      } else {
        index = Math.max(0, Math.min(index, groups.length - 1));
      }

      this.activeIndex = index;
      this.isProgrammatic = true;
      this.viewport.scrollTo({
        left: this.desiredScrollLeft(index),
        behavior: smooth ? 'smooth' : 'auto'
      });
      this.update();
      window.setTimeout(() => { this.isProgrammatic = false; }, 250);
    }

    updateFromScroll() {
      const groups = this.groups;
      if (!groups.length) return;

      if (!this.isProgrammatic) {
        let nearest = 0;
        let nearestDistance = Infinity;
        groups.forEach((group, index) => {
          const distance = Math.abs(this.viewport.scrollLeft - this.desiredScrollLeft(index));
          if (distance < nearestDistance) {
            nearestDistance = distance;
            nearest = index;
          }
        });
        this.activeIndex = nearest;
      }

      this.update();
    }

    update() {
      const groups = this.groups;
      if (!groups.length) return;

      const hasPrev = this.activeIndex > 0;
      const hasNext = this.activeIndex < groups.length - 1;

      this.root.classList.toggle('has-prev', hasPrev);
      this.root.classList.toggle('has-next', hasNext);

      groups.forEach((group, index) => {
      const isActive = index === this.activeIndex;

      let isPrevPreview = index === this.activeIndex - 1;
      let isNextPreview = index === this.activeIndex + 1;

      if (this.isMobile()) {
        const isLastActive = this.activeIndex === groups.length - 1;

        // Mobile:
        // Group 1 and middle groups show next preview only.
        // Last group shows previous preview only.
        isPrevPreview = isLastActive && index === this.activeIndex - 1;
        isNextPreview = !isLastActive && index === this.activeIndex + 1;
      }

      const isPreview = !isActive && (isPrevPreview || isNextPreview);

      group.classList.toggle('is-active', isActive);
      group.classList.toggle('is-preview', isPreview);
      group.classList.toggle('is-prev-preview', isPreview && isPrevPreview);
      group.classList.toggle('is-next-preview', isPreview && isNextPreview);
      group.setAttribute('aria-hidden', isActive || isPreview ? 'false' : 'true');
    });

      if (this.prevButton) this.prevButton.disabled = !this.loop && !hasPrev;
      if (this.nextButton) this.nextButton.disabled = !this.loop && !hasNext;
    }
  }

  function init() {
    document.querySelectorAll(SELECTORS.root).forEach((root) => {
      if (!root.__mainPillarRibbon) {
        root.__mainPillarRibbon = new MainPillarRibbon(root);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
